/**
 * TeamStrength Hub - Messaging Center Component
 * Manages email templates, history, and bulk sending
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import { messagingApi } from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { 
  Mail, Send, FileText, History, Settings, CheckCircle2, XCircle, 
  RefreshCw, Eye, Edit2, RotateCcw, Users, AlertTriangle, Loader2
} from 'lucide-react';

// Translations
const t = {
  de: {
    title: 'Messaging Center',
    subtitle: 'E-Mails verwalten und versenden',
    tabs: {
      history: 'Verlauf',
      templates: 'Templates',
      send: 'Versenden'
    },
    history: {
      title: 'E-Mail-Verlauf',
      subtitle: 'Alle gesendeten E-Mails',
      noLogs: 'Noch keine E-Mails gesendet',
      recipient: 'Empfänger',
      type: 'Typ',
      subject: 'Betreff',
      date: 'Datum',
      status: 'Status',
      sent: 'Gesendet',
      failed: 'Fehlgeschlagen',
      filter: 'Nach Typ filtern',
      all: 'Alle'
    },
    templates: {
      title: 'E-Mail-Templates',
      subtitle: 'Texte der automatischen E-Mails anpassen',
      preview: 'Vorschau',
      edit: 'Bearbeiten',
      reset: 'Zurücksetzen',
      save: 'Speichern',
      cancel: 'Abbrechen',
      custom: 'Angepasst',
      default: 'Standard',
      subject: 'Betreff',
      heading: 'Überschrift',
      body: 'Inhalt',
      button: 'Button-Text',
      german: 'Deutsch',
      english: 'Englisch',
      variables: 'Verfügbare Variablen',
      resetConfirm: 'Template wirklich zurücksetzen?'
    },
    send: {
      title: 'E-Mails versenden',
      subtitle: 'Nachrichten an Org-Mitglieder senden',
      profileReminder: 'Profil-Erinnerung',
      profileReminderDesc: 'An alle Nutzer ohne ausgefüllte Stärken',
      customMessage: 'Benutzerdefinierte Nachricht',
      customMessageDesc: 'Freitext an alle Mitglieder',
      subject: 'Betreff',
      message: 'Nachricht',
      send: 'Jetzt senden',
      sending: 'Wird gesendet...',
      targetUsers: 'Zielgruppe',
      usersWithoutStrengths: 'Nutzer ohne Stärken',
      allMembers: 'Alle Mitglieder',
      successMsg: 'E-Mails gesendet',
      noTargetUsers: 'Keine Nutzer ohne Stärken gefunden'
    },
    types: {
      invitation: 'Einladung',
      welcome: 'Willkommen',
      password_reset: 'Passwort-Reset',
      team_added: 'Team-Hinzufügung',
      profile_reminder: 'Profil-Erinnerung',
      custom: 'Benutzerdefiniert'
    }
  },
  en: {
    title: 'Messaging Center',
    subtitle: 'Manage and send emails',
    tabs: {
      history: 'History',
      templates: 'Templates',
      send: 'Send'
    },
    history: {
      title: 'Email History',
      subtitle: 'All sent emails',
      noLogs: 'No emails sent yet',
      recipient: 'Recipient',
      type: 'Type',
      subject: 'Subject',
      date: 'Date',
      status: 'Status',
      sent: 'Sent',
      failed: 'Failed',
      filter: 'Filter by type',
      all: 'All'
    },
    templates: {
      title: 'Email Templates',
      subtitle: 'Customize automatic email texts',
      preview: 'Preview',
      edit: 'Edit',
      reset: 'Reset',
      save: 'Save',
      cancel: 'Cancel',
      custom: 'Custom',
      default: 'Default',
      subject: 'Subject',
      heading: 'Heading',
      body: 'Body',
      button: 'Button Text',
      german: 'German',
      english: 'English',
      variables: 'Available Variables',
      resetConfirm: 'Really reset template?'
    },
    send: {
      title: 'Send Emails',
      subtitle: 'Send messages to org members',
      profileReminder: 'Profile Reminder',
      profileReminderDesc: 'To all users without strengths',
      customMessage: 'Custom Message',
      customMessageDesc: 'Free text to all members',
      subject: 'Subject',
      message: 'Message',
      send: 'Send Now',
      sending: 'Sending...',
      targetUsers: 'Target',
      usersWithoutStrengths: 'Users without strengths',
      allMembers: 'All members',
      successMsg: 'Emails sent',
      noTargetUsers: 'No users without strengths found'
    },
    types: {
      invitation: 'Invitation',
      welcome: 'Welcome',
      password_reset: 'Password Reset',
      team_added: 'Team Added',
      profile_reminder: 'Profile Reminder',
      custom: 'Custom'
    }
  }
};

// Template variables by type
const TEMPLATE_VARIABLES = {
  invitation: ['{org_name}', '{inviter_name}', '{role}'],
  welcome: ['{user_name}', '{org_name}'],
  password_reset: ['{user_name}'],
  team_added: ['{user_name}', '{team_name}', '{org_name}', '{added_by}'],
  profile_reminder: ['{user_name}', '{org_name}']
};

const MessagingCenter = () => {
  const { currentOrg, user } = useAuth();
  const lang = user?.preferred_language || 'de';
  const txt = t[lang];
  
  const [activeTab, setActiveTab] = useState('history');
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [templates, setTemplates] = useState({});
  const [stats, setStats] = useState(null);
  const [filterType, setFilterType] = useState('all');
  
  // Template editing
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editedTemplate, setEditedTemplate] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLang, setPreviewLang] = useState(lang);
  
  // Sending
  const [sendType, setSendType] = useState('profile_reminder');
  const [customSubject, setCustomSubject] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [sending, setSending] = useState(false);

  // Load data
  useEffect(() => {
    if (!currentOrg) return;
    loadData();
  }, [currentOrg]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [logsRes, templatesRes, statsRes] = await Promise.all([
        messagingApi.getLogs(currentOrg.org_id),
        messagingApi.getTemplates(currentOrg.org_id),
        messagingApi.getStats(currentOrg.org_id)
      ]);
      setLogs(logsRes.data.logs || []);
      setTemplates(templatesRes.data.templates || {});
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to load messaging data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  // Filter logs
  const filteredLogs = filterType === 'all' 
    ? logs 
    : logs.filter(l => l.email_type === filterType);

  // Preview template
  const handlePreview = async (templateType) => {
    try {
      const res = await messagingApi.previewTemplate(currentOrg.org_id, templateType, previewLang);
      setPreviewHtml(res.data.html);
    } catch (error) {
      toast.error('Vorschau fehlgeschlagen');
    }
  };

  // Edit template
  const handleEditTemplate = (templateType) => {
    setEditingTemplate(templateType);
    setEditedTemplate(JSON.parse(JSON.stringify(templates[templateType])));
  };

  // Save template
  const handleSaveTemplate = async () => {
    try {
      await messagingApi.updateTemplate(currentOrg.org_id, editingTemplate, editedTemplate);
      toast.success('Template gespeichert');
      setEditingTemplate(null);
      loadData();
    } catch (error) {
      toast.error('Speichern fehlgeschlagen');
    }
  };

  // Reset template
  const handleResetTemplate = async (templateType) => {
    if (!window.confirm(txt.templates.resetConfirm)) return;
    try {
      await messagingApi.resetTemplate(currentOrg.org_id, templateType);
      toast.success('Template zurückgesetzt');
      loadData();
    } catch (error) {
      toast.error('Zurücksetzen fehlgeschlagen');
    }
  };

  // Send emails
  const handleSend = async () => {
    if (sendType === 'profile_reminder' && stats?.users_without_strengths === 0) {
      toast.info(txt.send.noTargetUsers);
      return;
    }
    
    if (sendType === 'custom' && (!customSubject || !customMessage)) {
      toast.error('Betreff und Nachricht erforderlich');
      return;
    }
    
    setSending(true);
    try {
      const payload = sendType === 'custom' 
        ? { type: 'custom', subject: customSubject, message: customMessage }
        : { type: 'profile_reminder' };
      
      const res = await messagingApi.send(currentOrg.org_id, payload);
      toast.success(`${txt.send.successMsg}: ${res.data.sent} gesendet, ${res.data.failed} fehlgeschlagen`);
      setCustomSubject('');
      setCustomMessage('');
      loadData();
    } catch (error) {
      toast.error('Senden fehlgeschlagen');
    } finally {
      setSending(false);
    }
  };

  if (!currentOrg) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Mail className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">Wähle eine Organisation</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Manrope' }}>
            {txt.title}
          </h1>
          <p className="text-slate-500 dark:text-slate-400">{txt.subtitle}</p>
        </div>
        <Button variant="outline" onClick={loadData} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Aktualisieren
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-violet-100 dark:bg-violet-900/30 rounded-lg">
                  <Mail className="w-5 h-5 text-violet-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_emails}</p>
                  <p className="text-sm text-slate-500">E-Mails gesamt</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {Object.values(stats.by_type || {}).reduce((a, b) => a + (b.sent || 0), 0)}
                  </p>
                  <p className="text-sm text-slate-500">Erfolgreich</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.users_without_strengths}</p>
                  <p className="text-sm text-slate-500">Ohne Stärken</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-cyan-100 dark:bg-cyan-900/30 rounded-lg">
                  <Users className="w-5 h-5 text-cyan-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_members}</p>
                  <p className="text-sm text-slate-500">Mitglieder</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="history" className="gap-2">
            <History className="w-4 h-4" />
            {txt.tabs.history}
          </TabsTrigger>
          <TabsTrigger value="templates" className="gap-2">
            <FileText className="w-4 h-4" />
            {txt.tabs.templates}
          </TabsTrigger>
          <TabsTrigger value="send" className="gap-2">
            <Send className="w-4 h-4" />
            {txt.tabs.send}
          </TabsTrigger>
        </TabsList>

        {/* History Tab */}
        <TabsContent value="history" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>{txt.history.title}</CardTitle>
                  <CardDescription>{txt.history.subtitle}</CardDescription>
                </div>
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder={txt.history.filter} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{txt.history.all}</SelectItem>
                    {Object.keys(txt.types).map(type => (
                      <SelectItem key={type} value={type}>{txt.types[type]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-3">
                  {[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full" />)}
                </div>
              ) : filteredLogs.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>{txt.history.noLogs}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-2 text-sm font-medium text-slate-500">{txt.history.recipient}</th>
                        <th className="text-left py-3 px-2 text-sm font-medium text-slate-500">{txt.history.type}</th>
                        <th className="text-left py-3 px-2 text-sm font-medium text-slate-500">{txt.history.subject}</th>
                        <th className="text-left py-3 px-2 text-sm font-medium text-slate-500">{txt.history.date}</th>
                        <th className="text-left py-3 px-2 text-sm font-medium text-slate-500">{txt.history.status}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredLogs.map((log, idx) => (
                        <tr key={idx} className="border-b last:border-0 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                          <td className="py-3 px-2 text-sm">{log.recipient}</td>
                          <td className="py-3 px-2">
                            <Badge variant="outline">{txt.types[log.email_type] || log.email_type}</Badge>
                          </td>
                          <td className="py-3 px-2 text-sm max-w-xs truncate">{log.subject}</td>
                          <td className="py-3 px-2 text-sm text-slate-500">
                            {new Date(log.sent_at).toLocaleString(lang === 'de' ? 'de-DE' : 'en-US')}
                          </td>
                          <td className="py-3 px-2">
                            {log.status === 'sent' ? (
                              <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                {txt.history.sent}
                              </Badge>
                            ) : (
                              <Badge variant="destructive">
                                <XCircle className="w-3 h-3 mr-1" />
                                {txt.history.failed}
                              </Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="mt-6">
          <div className="grid gap-4">
            {Object.entries(templates).map(([type, template]) => (
              <Card key={type}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        {txt.types[type]}
                        {template.is_custom ? (
                          <Badge className="bg-violet-100 text-violet-700 hover:bg-violet-100">
                            {txt.templates.custom}
                          </Badge>
                        ) : (
                          <Badge variant="outline">{txt.templates.default}</Badge>
                        )}
                      </CardTitle>
                    </div>
                    <div className="flex gap-2">
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="outline" size="sm" onClick={() => handlePreview(type)}>
                            <Eye className="w-4 h-4 mr-1" />
                            {txt.templates.preview}
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-3xl max-h-[80vh] overflow-auto">
                          <DialogHeader>
                            <DialogTitle>E-Mail Vorschau: {txt.types[type]}</DialogTitle>
                          </DialogHeader>
                          <div className="flex gap-2 mb-4">
                            <Button 
                              variant={previewLang === 'de' ? 'default' : 'outline'} 
                              size="sm"
                              onClick={() => { setPreviewLang('de'); handlePreview(type); }}
                            >
                              🇩🇪 DE
                            </Button>
                            <Button 
                              variant={previewLang === 'en' ? 'default' : 'outline'} 
                              size="sm"
                              onClick={() => { setPreviewLang('en'); handlePreview(type); }}
                            >
                              🇬🇧 EN
                            </Button>
                          </div>
                          <div 
                            className="border rounded-lg p-4 bg-white"
                            dangerouslySetInnerHTML={{ __html: previewHtml }}
                          />
                        </DialogContent>
                      </Dialog>
                      <Button variant="outline" size="sm" onClick={() => handleEditTemplate(type)}>
                        <Edit2 className="w-4 h-4 mr-1" />
                        {txt.templates.edit}
                      </Button>
                      {template.is_custom && (
                        <Button variant="ghost" size="sm" onClick={() => handleResetTemplate(type)}>
                          <RotateCcw className="w-4 h-4 mr-1" />
                          {txt.templates.reset}
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="font-medium text-slate-500 mb-1">🇩🇪 {txt.templates.subject}</p>
                      <p className="text-slate-700 dark:text-slate-300">{template.de?.subject}</p>
                    </div>
                    <div>
                      <p className="font-medium text-slate-500 mb-1">🇬🇧 {txt.templates.subject}</p>
                      <p className="text-slate-700 dark:text-slate-300">{template.en?.subject}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Edit Template Dialog */}
          <Dialog open={!!editingTemplate} onOpenChange={() => setEditingTemplate(null)}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto">
              <DialogHeader>
                <DialogTitle>Template bearbeiten: {txt.types[editingTemplate]}</DialogTitle>
              </DialogHeader>
              {editedTemplate && (
                <div className="space-y-6">
                  {/* Variables */}
                  <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <p className="text-sm font-medium text-slate-500 mb-2">{txt.templates.variables}:</p>
                    <div className="flex flex-wrap gap-2">
                      {(TEMPLATE_VARIABLES[editingTemplate] || []).map(v => (
                        <code key={v} className="px-2 py-1 bg-violet-100 text-violet-700 rounded text-xs">
                          {v}
                        </code>
                      ))}
                    </div>
                  </div>

                  {/* German */}
                  <div className="space-y-4">
                    <h3 className="font-medium flex items-center gap-2">🇩🇪 {txt.templates.german}</h3>
                    <div className="space-y-3">
                      <div>
                        <Label>{txt.templates.subject}</Label>
                        <Input
                          value={editedTemplate.de?.subject || ''}
                          onChange={(e) => setEditedTemplate({
                            ...editedTemplate,
                            de: { ...editedTemplate.de, subject: e.target.value }
                          })}
                        />
                      </div>
                      <div>
                        <Label>{txt.templates.heading}</Label>
                        <Input
                          value={editedTemplate.de?.heading || ''}
                          onChange={(e) => setEditedTemplate({
                            ...editedTemplate,
                            de: { ...editedTemplate.de, heading: e.target.value }
                          })}
                        />
                      </div>
                      <div>
                        <Label>{txt.templates.body}</Label>
                        <Textarea
                          value={editedTemplate.de?.body || ''}
                          onChange={(e) => setEditedTemplate({
                            ...editedTemplate,
                            de: { ...editedTemplate.de, body: e.target.value }
                          })}
                          rows={4}
                        />
                      </div>
                      <div>
                        <Label>{txt.templates.button}</Label>
                        <Input
                          value={editedTemplate.de?.button || ''}
                          onChange={(e) => setEditedTemplate({
                            ...editedTemplate,
                            de: { ...editedTemplate.de, button: e.target.value }
                          })}
                        />
                      </div>
                    </div>
                  </div>

                  {/* English */}
                  <div className="space-y-4">
                    <h3 className="font-medium flex items-center gap-2">🇬🇧 {txt.templates.english}</h3>
                    <div className="space-y-3">
                      <div>
                        <Label>{txt.templates.subject}</Label>
                        <Input
                          value={editedTemplate.en?.subject || ''}
                          onChange={(e) => setEditedTemplate({
                            ...editedTemplate,
                            en: { ...editedTemplate.en, subject: e.target.value }
                          })}
                        />
                      </div>
                      <div>
                        <Label>{txt.templates.heading}</Label>
                        <Input
                          value={editedTemplate.en?.heading || ''}
                          onChange={(e) => setEditedTemplate({
                            ...editedTemplate,
                            en: { ...editedTemplate.en, heading: e.target.value }
                          })}
                        />
                      </div>
                      <div>
                        <Label>{txt.templates.body}</Label>
                        <Textarea
                          value={editedTemplate.en?.body || ''}
                          onChange={(e) => setEditedTemplate({
                            ...editedTemplate,
                            en: { ...editedTemplate.en, body: e.target.value }
                          })}
                          rows={4}
                        />
                      </div>
                      <div>
                        <Label>{txt.templates.button}</Label>
                        <Input
                          value={editedTemplate.en?.button || ''}
                          onChange={(e) => setEditedTemplate({
                            ...editedTemplate,
                            en: { ...editedTemplate.en, button: e.target.value }
                          })}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setEditingTemplate(null)}>
                      {txt.templates.cancel}
                    </Button>
                    <Button onClick={handleSaveTemplate} className="bg-violet-600 hover:bg-violet-700">
                      {txt.templates.save}
                    </Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Send Tab */}
        <TabsContent value="send" className="mt-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Profile Reminder */}
            <Card className={sendType === 'profile_reminder' ? 'ring-2 ring-violet-500' : ''}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={sendType === 'profile_reminder'}
                    onChange={() => setSendType('profile_reminder')}
                    className="w-4 h-4"
                  />
                  {txt.send.profileReminder}
                </CardTitle>
                <CardDescription>{txt.send.profileReminderDesc}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                    <span className="font-medium">{txt.send.targetUsers}:</span>
                  </div>
                  <Badge variant="outline" className="text-lg">
                    {stats?.users_without_strengths || 0} {txt.send.usersWithoutStrengths}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* Custom Message */}
            <Card className={sendType === 'custom' ? 'ring-2 ring-violet-500' : ''}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={sendType === 'custom'}
                    onChange={() => setSendType('custom')}
                    className="w-4 h-4"
                  />
                  {txt.send.customMessage}
                </CardTitle>
                <CardDescription>{txt.send.customMessageDesc}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>{txt.send.subject}</Label>
                  <Input
                    value={customSubject}
                    onChange={(e) => setCustomSubject(e.target.value)}
                    placeholder="Wichtige Mitteilung..."
                    disabled={sendType !== 'custom'}
                  />
                </div>
                <div>
                  <Label>{txt.send.message}</Label>
                  <Textarea
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value)}
                    placeholder="Deine Nachricht..."
                    rows={4}
                    disabled={sendType !== 'custom'}
                  />
                </div>
                <div className="flex items-center justify-between p-4 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-cyan-600" />
                    <span className="font-medium">{txt.send.targetUsers}:</span>
                  </div>
                  <Badge variant="outline" className="text-lg">
                    {stats?.total_members || 0} {txt.send.allMembers}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Send Button */}
          <div className="flex justify-center mt-8">
            <Button
              size="lg"
              onClick={handleSend}
              disabled={sending}
              className="bg-violet-600 hover:bg-violet-700 px-8"
            >
              {sending ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  {txt.send.sending}
                </>
              ) : (
                <>
                  <Send className="w-5 h-5 mr-2" />
                  {txt.send.send}
                </>
              )}
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MessagingCenter;
